import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useMoodleV2 } from "../../contexts/moodle";
import { isDuelsEnabled } from "../../utils/featureFlags";
import {
  useDuelsList,
  useChallenge,
  useDuelsHistory,
  useUserStats,
  duelKeys,
} from "../../services/moodle/duel.queries";
import { Footer } from "../../components/Footer";
import { OpponentPickerModal } from "../../components/OpponentPicker/OpponentPickerModal";
import { DuelsList } from "./DuelsList";
import { DuelsTabs } from "./DuelsTabs";
import { DuelsHistoryList } from "./DuelsHistoryList";
import { DUEL_HISTORY_MOCK } from "./duelHistoryMock";
import { ActiveDuel } from "./ActiveDuel";
import { DuelReviewModal } from "./DuelReviewModal";
import {
  getActiveChallengeId,
  setActiveChallengeId,
  clearActiveChallengeId,
} from "./activeChallengeStorage";
import aduanaTheme from "../../components/DuelSkin/aduanaTheme";
import "../../components/DuelSkin/aduanaSkin.css";

const { labels } = aduanaTheme;

/**
 * Statuses considered "still active" for a hydrated challenge (design.md
 * D6). Anything outside this set (or a hydration error, or an unexpected/
 * unrecognized response shape from `local_tipddy_duel_get_challenge` — see
 * risk note below) is treated as a stale/invalid `activeChallengeId` and
 * triggers the non-blocking recovery path.
 */
const ACTIVE_STATUSES = ["pending", "accepted", "playing"];

/**
 * DuelsPage — route element for `/duels` (design.md D1). The single
 * stateful node of the feature: owns `activeId` (which pending duel is
 * currently open in `ActiveDuel`) and `pickerOpen` (whether
 * `OpponentPickerModal` is showing). Data comes from the shared
 * `useDuelsList` hook (spec `duel-data-layer` — "Shared Pending-Duels Query
 * Key"), the same one `DuelBadge` consumes, so both stay in sync via
 * react-query's key-based cache.
 *
 * `activeId` is hydrated from `sessionStorage` on mount (spec
 * `duel-spa-navigation` — "Active Challenge Persists Via sessionStorage")
 * and kept in sync with it on every change. If the persisted id is not
 * already present in the shared `useDuelsList` data (e.g. a cold reload),
 * it is hydrated via `useChallenge`/`getChallenge` (spec — "Mount-Time
 * Hydration"). A stale/invalid/unexpected hydration result clears the
 * persisted id and shows a non-blocking recovery banner instead of
 * blocking the page (design.md D6).
 *
 * `OpponentPickerModal` is rendered directly here per design.md D1 — its
 * `SENT` state stays terminal and never hands off to `ActiveDuel`/`BattleModal`.
 */
const DuelsPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const moodlev2 = useMoodleV2();
  const queryClient = useQueryClient();
  const duelsEnabled = isDuelsEnabled();

  const token = auth?.user?.token || localStorage.getItem("token");
  const currentUserId = auth?.user?.id;

  const [activeId, setActiveIdState] = useState(() => getActiveChallengeId());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reviewChallengeId, setReviewChallengeId] = useState(null);
  const [staleNotice, setStaleNotice] = useState(false);
  const initialPersistedIdRef = useRef(getActiveChallengeId());
  const lastActiveChallengeRef = useRef(null);
  // duel-history-provisional (Phase 4, design.md D1): local state only — no
  // sessionStorage/URL persistence, so "Activos" is always the tab on
  // mount/remount (spec Req 1/3, "Historial Is Never Auto-Selected").
  const [activeTab, setActiveTab] = useState("active");

  const { data: duels, isLoading, isError } = useDuelsList({
    token,
    currentUserId,
    enabled: duelsEnabled && !!token,
  });

  const listChallenge = useMemo(
    () => (duels || []).find((duel) => duel.id === activeId) || null,
    [duels, activeId]
  );

  useEffect(() => {
    if (listChallenge) {
      lastActiveChallengeRef.current = listChallenge;
      initialPersistedIdRef.current = null;
    }
  }, [listChallenge]);

  // Mount-time hydration for a persisted `activeChallengeId` that isn't
  // (yet) part of the shared pending-duels list — spec `duel-spa-navigation`
  // — "Mount-Time Hydration". Only fetched when the list hasn't already
  // resolved the challenge, to avoid a redundant request.
  const {
    data: hydratedChallenge,
    isError: isHydrationError,
    isFetched: isHydrationFetched,
  } = useChallenge({
    token,
    challengeId: activeId,
    currentUserId,
    enabled: duelsEnabled && !!activeId && !isLoading && !listChallenge,
  });

  useEffect(() => {
    if (hydratedChallenge) {
      lastActiveChallengeRef.current = hydratedChallenge;
    }
  }, [hydratedChallenge]);

  const activeChallenge =
    listChallenge ||
    hydratedChallenge ||
    (lastActiveChallengeRef.current?.id === activeId ? lastActiveChallengeRef.current : null);

  const selectDuel = (id) => {
    setStaleNotice(false);
    initialPersistedIdRef.current = null;
    setActiveIdState(id);
    setActiveChallengeId(id);
  };

  // Clears the persisted/in-memory selection and invalidates the shared
  // pending-duels query so both this page and DuelBadge reflect the
  // post-battle state before the next poll tick (spec `duel-data-layer` —
  // "Mutation-Driven Invalidation").
  const handleActiveDuelSettled = () => {
    setActiveIdState(null);
    clearActiveChallengeId();
    lastActiveChallengeRef.current = null;
    initialPersistedIdRef.current = null;
    queryClient.invalidateQueries({ queryKey: duelKeys.all });
  };

  // Called when attempt submission finishes. Updates cache without unmounting
  // the modal prematurely, allowing the user to view RESULT or WAITING_OPPONENT.
  const handleDuelFinished = () => {
    // Intentionally no-op: do not invalidate active list queries while the modal
    // is open, which would drop the duel from listChallenge and trigger premature unmount.
    // Invalidation occurs in handleActiveDuelSettled when the user closes the modal.
  };

  const isQaMode = process.env.REACT_APP_QA_MODE === "true";
  const { data: realHistoryDuels } = useDuelsHistory({
    token,
    currentUserId,
    enabled: isQaMode && !!token && activeTab === "history",
  });
  const { data: realUserStats } = useUserStats({
    token,
    userid: currentUserId,
    enabled: isQaMode && !!token && activeTab === "history",
  });

  const isRealHistory = Boolean(isQaMode && realHistoryDuels && realHistoryDuels.length > 0);
  const historyStats = isRealHistory && realUserStats ? realUserStats : DUEL_HISTORY_MOCK.stats;
  const historyDuels = isRealHistory ? realHistoryDuels : DUEL_HISTORY_MOCK.duels;

  // Stale/invalid `activeChallengeId` recovery (design.md D6). Treats a
  // hydration error, a missing result, AND any status outside
  // `ACTIVE_STATUSES` as non-recoverable ONLY for cold-hydrated persisted IDs.
  // When a duel was active in this session, reaching 'finished' must not trigger stale recovery.
  useEffect(() => {
    if (!activeId || listChallenge || !isHydrationFetched) return;

    const isColdPersistedHydration = initialPersistedIdRef.current === activeId;

    const isStale =
      isHydrationError ||
      !hydratedChallenge ||
      (isColdPersistedHydration && !ACTIVE_STATUSES.includes(hydratedChallenge.status));

    if (isStale) {
      initialPersistedIdRef.current = null;
      lastActiveChallengeRef.current = null;
      clearActiveChallengeId();
      setActiveIdState(null);
      setStaleNotice(true);
    } else if (isColdPersistedHydration) {
      initialPersistedIdRef.current = null;
    }
  }, [activeId, listChallenge, hydratedChallenge, isHydrationError, isHydrationFetched]);

  const pendingCount = (duels || []).length;
  const tickerLabel =
    pendingCount === 0
      ? labels.activeOpsTickerNone
      : pendingCount === 1
      ? labels.activeOpsTickerOne
      : labels.activeOpsTickerMany.replace("{count}", pendingCount);

  if (!duelsEnabled) {
    return (
      <>
        <div
          className="duels-page-root duel-skin-root"
          style={{
            position: "relative",
            minHeight: "calc(100vh - 82px)",
            backgroundColor: "var(--duel-skin-void)",
            color: "var(--duel-skin-text)",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="duel-skin-radar-field">
            <div className="duel-skin-radar-sweep"></div>
            <div className="duel-skin-scanline"></div>
          </div>

          <div
            style={{
              position: "relative",
              zIndex: 1,
              maxWidth: "580px",
              margin: "0 auto",
              padding: "48px 24px",
              textAlign: "center",
            }}
          >
            <span className="duel-skin-tag">{labels.comingSoonTag || labels.duelsPageTag}</span>
            <h1
              className="duel-skin-hero-face"
              style={{
                fontSize: "clamp(26px, 4vw, 36px)",
                margin: "12px 0 16px",
                color: "var(--duel-skin-gold)",
              }}
            >
              {labels.comingSoonTitle || "Próximamente disponible"}
            </h1>
            <p
              style={{
                color: "var(--duel-skin-text-dim)",
                fontSize: "15px",
                lineHeight: 1.6,
                marginBottom: "28px",
              }}
            >
              {labels.comingSoonSubtitle ||
                "Estamos calibrando el sistema de duelos 1v1 y operativos de conocimiento. Muy pronto podrás desafiar a tus compañeros de sector."}
            </p>
            <button
              type="button"
              className="duel-skin-action-btn"
              onClick={() => navigate("/listcourses")}
              style={{ padding: "12px 28px", fontSize: "14px" }}
            >
              {labels.comingSoonBackAction || "Volver al inicio"}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="duel-skin-root" style={{ minHeight: "calc(100vh - 82px)" }}>
        {/* Radar backdrop — spec duel-visual-identity, ported 1:1 from
            OperativoAduanas so /duels carries the same atmosphere as the
            two duel modals instead of reading as a bare list. */}
        <div className="duel-skin-radar-field">
          <div className="duel-skin-radar-sweep"></div>
          <div className="duel-skin-scanline"></div>
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: "900px",
            margin: "0 auto",
            padding: "96px 16px 48px",
          }}
        >
          <span className="duel-skin-tag">{labels.duelsPageTag}</span>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "10px",
              marginTop: "8px",
            }}
          >
            <h1 className="duel-skin-hero-face" style={{ fontSize: "clamp(28px,4.5vw,44px)", margin: 0 }}>
              {labels.duelsPageTitle}
            </h1>
            <button
              type="button"
              className="duel-skin-action-btn"
              onClick={() => setPickerOpen(true)}
            >
              {labels.newChallengeAction}
            </button>
          </div>

          <p style={{ color: "var(--duel-skin-text-dim)", maxWidth: "56ch", marginBottom: "16px" }}>
            {labels.duelsPageSubtitle}
          </p>

          <div className={`duel-skin-hud-ticker${pendingCount === 0 ? " idle" : ""}`} style={{ marginBottom: "18px" }}>
            <span className="duel-skin-blip"></span>
            {tickerLabel}
          </div>

          {staleNotice && (
            <div className="duel-skin-error-banner">⚠️ {labels.staleChallengeNotice}</div>
          )}

          {/* duel-history-provisional (Phase 4, design.md D5/D8): tab rail is
              secondary/lighter weight than the "Nuevo desafío" CTA (spec
              Req 2, "CTA Remains the Dominant Control") — it never renders
              `.duel-skin-action-btn`. Exactly one panel is mounted at a
              time; the inactive panel is unmounted, not hidden, so
              switching tabs can never trigger a network request
              (spec Req 9, design.md D8). */}
          <div style={{ marginBottom: "22px" }}>
            <DuelsTabs activeTab={activeTab} onChange={setActiveTab} />
          </div>

          {activeTab === "active" ? (
            <div id="duel-tabpanel-active" role="tabpanel" aria-labelledby="duel-tab-active">
              <DuelsList
                duels={duels}
                isLoading={isLoading}
                isError={isError}
                onSelect={selectDuel}
              />
            </div>
          ) : (
            <div id="duel-tabpanel-history" role="tabpanel" aria-labelledby="duel-tab-history">
              <DuelsHistoryList
                stats={historyStats}
                duels={historyDuels}
                isMock={!isRealHistory}
                onSelect={(duel) => {
                  if (duel?.id) {
                    setReviewChallengeId(duel.id);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
      <Footer />

      <ActiveDuel
        challenge={activeChallenge}
        token={token}
        currentUserId={currentUserId}
        onClose={handleActiveDuelSettled}
        onFinish={handleDuelFinished}
      />

      <OpponentPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        token={token}
        courses={moodlev2.courses}
        getCourses={moodlev2.getCourses}
      />

      <DuelReviewModal
        isOpen={Boolean(reviewChallengeId)}
        challengeId={reviewChallengeId}
        token={token}
        onClose={() => setReviewChallengeId(null)}
      />
    </>
  );
};

export default DuelsPage;
